import { GroupBase, StylesConfig } from 'react-select'

import { SelectOptionInterface } from '@customTypes'
import { palette, transitions } from '_styles/variables'

interface StylesOption {
  slim?: boolean
  hasError?: boolean
  float?: boolean
  hasNoOptionsMessage?: boolean
}

export const getStyles = <
  Opt extends SelectOptionInterface,
  IsMulti extends boolean,
  Group extends GroupBase<Opt>
>({
  slim,
  hasError,
  float,
  hasNoOptionsMessage,
}: StylesOption): StylesConfig<Opt, IsMulti, Group> => {
  const conditionalStyles = {
    valuContainer: float
      ? {
          alignSelf: 'flex-end',
          paddingBottom: '0.5rem',
          alignItems: 'flex-end',
        }
      : {},
    singleValue: float
      ? {
          lineHeight: '1em',
        }
      : {},
    input: float
      ? {
          lineHeight: '1em',
          marginBottom: 0,
          paddingBottom: 0,
          fontWeight: 600,
        }
      : {},
    noOptionsMessage: hasNoOptionsMessage
      ? {
          textAlign: 'left',
        }
      : {},
  }

  return {
    control: provided => ({
      ...provided,
      cursor: 'pointer',
      minHeight: slim ? 'unset' : '3.125rem',
      transition: transitions.quick('border-color'),
      boxShadow: 'none',
      ':focus': {
        borderColor: palette.ui.blue,
      },
      lineHeight: slim ? '1rem' : '2rem',
      borderColor: hasError ? palette.ui.error : palette.component.border,
      ' :focus': {
        backgroundColor: 'red',
      },
    }),
    multiValueRemove: provided => ({
      ...provided,
      ':hover': {
        backgroundColor: 'transparent',
      },
    }),
    placeholder: provided => ({
      ...provided,
      color: palette.neutral['grey-bbbdbf'],
      position: 'absolute',
    }),
    option: ({ padding, ...provided }, state) => ({
      ...provided,
      transition: transitions.instant('background'),
      paddingTop: state.isMulti ? '0.5rem' : '0.625rem',
      paddingLeft: state.isMulti ? '0.75rem' : '1rem',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: palette['form-field']['text-active-text'],
      ':hover': {
        transitionDelay: '60ms',
        backgroundColor: palette.component['background-highlight'],
      },
      ':active': {
        backgroundColor: palette.component['background-highlight'],
      },
      ':focus': {
        backgroundColor: palette.neutral.white,
      },
      backgroundColor:
        !state.isSelected || state.isMulti
          ? 'transparent'
          : palette.component['background-highlight'],
      '> label': {
        maxWidth: '100%',
        '> p': {
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
    }),
    valueContainer: ({ padding, ...provided }) => ({
      ...provided,
      flex: 1,
      display: 'flex',
      overflow: 'hidden',
      flexWrap: 'nowrap',
      height: '3rem',
      position: 'relative',
      ...conditionalStyles.valuContainer,
    }),
    multiValueLabel: provided => ({
      ...provided,
      lineHeight: '1rem',
    }),
    singleValue: provided => ({
      ...provided,
      fontWeight: 600,
      lineHeight: '1.25rem',
      ...conditionalStyles.singleValue,
    }),
    multiValue: provided => ({
      ...provided,
      fontWeight: 600,
      lineHeight: '1.25rem',
    }),
    input: provided => ({
      ...provided,
      flex: '2 0 auto',
      ...conditionalStyles.input,
    }),
    menuList: provided => ({
      ...provided,
      paddingTop: '0.25rem',
    }),
    group: () => ({
      boxSizing: 'border-box',
    }),
    groupHeading: ({ marginBottom, ...provided }) => ({
      ...provided,
      padding: '0.5rem 0.75rem',
    }),
    noOptionsMessage: provided => ({
      ...provided,
      ...conditionalStyles.noOptionsMessage,
    }),
  }
}
